import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import PermissionLevelEditor from '../components/PermissionLevelEditor';
import {
  Shield,
  Plus,
  Edit2,
  Trash2,
  Users,
  Eye,
  ChevronDown,
  ChevronRight,
  Star,
  Copy,
  UserPlus,
  X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PermissionManagementV2({ embedded = false }) {
  const [levels, setLevels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showUserList, setShowUserList] = useState(null);
  const [viewAsLoading, setViewAsLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [levelsRes, usersRes] = await Promise.all([
        axios.get(`${API}/permission-levels`),
        axios.get(`${API}/users`),
      ]);
      setLevels(levelsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load permission levels');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (levelId) => {
    const usersWithLevel = users.filter(u => u.permission_level_id === levelId);
    if (usersWithLevel.length > 0) {
      toast.error(`Cannot delete: ${usersWithLevel.length} users are assigned to this level`);
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this permission level?')) return;
    
    try {
      await axios.delete(`${API}/permission-levels/${levelId}`);
      toast.success('Permission level deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDuplicate = async (level) => {
    try {
      const newLevel = {
        name: `${level.name} (Copy)`,
        description: level.description,
        is_default: false,
        self_permissions: level.self_permissions,
        others_permissions: level.others_permissions,
        admin_permissions: level.admin_permissions,
        nav_permissions: level.nav_permissions,
      };
      await axios.post(`${API}/permission-levels`, newLevel);
      toast.success('Permission level duplicated');
      fetchData();
    } catch (error) {
      toast.error('Failed to duplicate');
    }
  };

  const handleViewAs = async (userId, userEmail) => {
    setViewAsLoading(userId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/users/view-as/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Store the new token and user info
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        toast.success(`Now viewing as ${userEmail}`);
        // Force reload to apply new permissions
        window.location.href = '/';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start view-as mode');
    } finally {
      setViewAsLoading(null);
    }
  };

  const getUsersForLevel = (levelId) => {
    return users.filter(u => u.permission_level_id === levelId);
  };

  const countPermissions = (level) => {
    let count = 0;
    
    // Count self permissions with view or edit
    Object.values(level.self_permissions || {}).forEach(v => {
      if (v === 'view' || v === 'edit') count++;
    });
    
    // Count others permissions
    Object.values(level.others_permissions || {}).forEach(v => {
      if (v === 'view' || v === 'edit') count++;
    });
    
    // Count admin permissions
    Object.values(level.admin_permissions || {}).forEach(v => {
      if (v === true) count++;
    });
    
    return count;
  };

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Loading...</div>;
  }

  // Show editor if creating or editing
  if (isCreating || selectedLevel) {
    return (
      <div className={embedded ? "" : "p-6"}>
        <PermissionLevelEditor
          levelId={selectedLevel?.id}
          isNew={isCreating}
          onSave={() => {
            setSelectedLevel(null);
            setIsCreating(false);
            fetchData();
          }}
          onCancel={() => {
            setSelectedLevel(null);
            setIsCreating(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-4" : "p-6 space-y-6"}>
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield size={28} />
              Permission Levels
            </h1>
            <p className="text-muted-foreground mt-1">
              Create and manage access levels for users (BambooHR-style)
            </p>
          </div>
          <Button onClick={() => setIsCreating(true)} data-testid="create-level-btn">
            <Plus size={16} className="mr-1" />
            Create Access Level
          </Button>
        </div>
      )}

      {embedded && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">Create and manage access levels for users</p>
          <Button size="sm" onClick={() => setIsCreating(true)} data-testid="create-level-btn">
            <Plus size={14} className="mr-1" />
            Create Access Level
          </Button>
        </div>
      )}

      {/* Permission Levels Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {levels.map(level => {
          const levelUsers = getUsersForLevel(level.id);
          const permCount = countPermissions(level);
          
          return (
            <Card key={level.id} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{level.name}</h3>
                    {level.is_default && (
                      <Badge className="bg-blue-100 text-blue-800 text-xs">
                        <Star size={10} className="mr-1" /> Default
                      </Badge>
                    )}
                  </div>
                  {level.description && (
                    <p className="text-sm text-muted-foreground mt-1">{level.description}</p>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                <span>{permCount} permissions enabled</span>
                <span 
                  className="flex items-center gap-1 cursor-pointer hover:text-foreground"
                  onClick={() => setShowUserList(showUserList === level.id ? null : level.id)}
                >
                  <Users size={14} />
                  {levelUsers.length} users
                  {showUserList === level.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
              </div>

              {/* User list (expandable) */}
              {showUserList === level.id && (
                <div className="mb-3 p-2 bg-muted/50 rounded text-sm max-h-48 overflow-y-auto">
                  {levelUsers.length === 0 ? (
                    <p className="text-muted-foreground">No users assigned</p>
                  ) : (
                    levelUsers.map(user => (
                      <div key={user.id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                        <span className="flex-1">{user.email}</span>
                        <div className="flex items-center gap-2">
                          <Select
                            value={user.permission_level_id || 'none'}
                            onValueChange={async (newLevelId) => {
                              if (newLevelId === 'none') return;
                              try {
                                const token = localStorage.getItem('token');
                                await axios.put(
                                  `${API}/users/${user.id}/permissions`,
                                  {
                                    level_id: newLevelId,
                                    use_custom: false,
                                    custom_permissions: user.custom_permissions || {},
                                  },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                toast.success(`Moved ${user.email} to new access level`);
                                fetchData();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to change access level');
                              }
                            }}
                          >
                            <SelectTrigger className="h-7 w-32 text-xs">
                              <SelectValue placeholder="Move to..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none" disabled>Move to...</SelectItem>
                              {levels.map(pl => (
                                <SelectItem key={pl.id} value={pl.id}>
                                  {pl.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => handleViewAs(user.id, user.email)}
                            disabled={viewAsLoading === user.id}
                          >
                            <Eye size={12} className="mr-1" />
                            {viewAsLoading === user.id ? '...' : 'View As'}
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Quick permission preview */}
              <div className="flex flex-wrap gap-1 mb-3">
                {level.admin_permissions?.admin_dashboard && (
                  <Badge variant="outline" className="text-xs">Dashboard</Badge>
                )}
                {level.admin_permissions?.admin_users && (
                  <Badge variant="outline" className="text-xs">Users</Badge>
                )}
                {level.admin_permissions?.admin_invoices && (
                  <Badge variant="outline" className="text-xs">Invoices</Badge>
                )}
                {level.nav_permissions?.personnel && (
                  <Badge variant="outline" className="text-xs">Personnel</Badge>
                )}
                {level.nav_permissions?.agencies && (
                  <Badge variant="outline" className="text-xs">Agencies</Badge>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => setSelectedLevel(level)}
                >
                  <Edit2 size={14} className="mr-1" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDuplicate(level)}
                  title="Duplicate"
                >
                  <Copy size={14} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handleDelete(level.id)}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {levels.length === 0 && (
        <Card className="p-8 text-center">
          <Shield className="mx-auto text-muted-foreground mb-4" size={48} />
          <h3 className="font-semibold mb-2">No Permission Levels</h3>
          <p className="text-muted-foreground mb-4">
            Create your first access level to control what users can see and do
          </p>
          <Button onClick={() => setIsCreating(true)}>
            <Plus size={16} className="mr-1" />
            Create Access Level
          </Button>
        </Card>
      )}
    </div>
  );
}
